import { api } from '@/lib/api/client';
import type {
  Role,
  RoleFilters,
  RolesResponse,
  CreateRoleDto,
  UpdateRoleDto,
} from '../types';

const ROLES_URL = '/roles';

export const rolesApi = {
  // Get all roles with filters
  getAll: async (filters?: RoleFilters): Promise<RolesResponse> => {
    const params: Record<string, unknown> = { ...filters };

    if (params['page'] !== undefined && params['limit']) {
      params['offset'] = (params['page'] as number) * (params['limit'] as number);
      delete params['page'];
    }

    // Handle search - only send if 2+ chars
    if (params['s'] && (params['s'] as string).length < 2) {
      delete params['s'];
    }

    // Handle date filters
    if (params['createdAt.min']) {
      const minDate = new Date(params['createdAt.min'] as string);
      minDate.setHours(0, 0, 0, 0);
      params['createdAt.min'] = minDate.toISOString();
    }

    if (params['createdAt.max']) {
      const maxDate = new Date(params['createdAt.max'] as string);
      maxDate.setHours(23, 59, 59, 999);
      params['createdAt.max'] = maxDate.toISOString();
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
      if (params[key] == null || params[key] === '') {
        delete params[key];
      }
    });

    // Remove order params if empty
    if (!params['orderBy'] || !params['order']) {
      delete params['orderBy'];
      delete params['order'];
    }

    return api.get<RolesResponse>(ROLES_URL, { params });
  },

  // Get single role by ID
  getById: async (id: number | string): Promise<Role> => {
    return api.get<Role>(`${ROLES_URL}/${id}`);
  },

  // Create new role
  create: async (data: CreateRoleDto): Promise<Role> => {
    return api.post<Role>(ROLES_URL, data);
  },

  // Update role
  update: async ({ id, data }: { id: number | string; data: UpdateRoleDto }): Promise<Role> => {
    return api.patch<Role>(`${ROLES_URL}/${id}`, data);
  },

  // Delete role
  delete: async (id: number | string): Promise<void> => {
    return api.delete(`${ROLES_URL}/${id}`);
  },

  // Activate role
  activate: async (id: number | string): Promise<Role> => {
    return api.put<Role>(`${ROLES_URL}/activate/${id}`, {});
  },

  // Deactivate role
  deactivate: async (id: number | string): Promise<Role> => {
    return api.put<Role>(`${ROLES_URL}/deactivate/${id}`, {});
  },
};

// Query keys for React Query
export const rolesQueryKeys = {
  all: ['roles'] as const,
  lists: () => [...rolesQueryKeys.all, 'list'] as const,
  list: (filters?: RoleFilters) => [...rolesQueryKeys.lists(), filters] as const,
  details: () => [...rolesQueryKeys.all, 'detail'] as const,
  detail: (id: number | string) => [...rolesQueryKeys.details(), id] as const,
};
